import isEmpty from 'lodash/isEmpty'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

import documents from 'modules/documents/store/collections/documents'
import type Employee from 'modules/employees/store/models/Employee'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Leave from 'modules/timeoff/store/models/Leave'

interface Props {
  employee: Employee | null | undefined
  form: LeaveForm
  documentForm: DocumentForm
  onLeaveSuccess: ((leave: Leave) => Promise<unknown>) | null | undefined
  onLeaveError: () => void
  getFieldsValues: () => any
  fetchStats: (leave: Leave) => void
  onCloseYear: () => void
  selectedEmployeeIds: number[]
  bulkAdd: boolean
}
interface ReturnType {
  onSubmit: (...args: any[]) => any
  isProcessing: boolean
}

const useCreateLeave = ({
  employee,
  form,
  documentForm,
  getFieldsValues,
  onLeaveSuccess,
  onLeaveError,
  fetchStats,
  onCloseYear,
  selectedEmployeeIds,
  bulkAdd,
}: Props): ReturnType => {
  const { fetchData } = useLeaves()

  const saveDocument = async (leave: Leave) => {
    if (isEmpty(documentForm.get('file').value)) return

    const customFields = getFieldsValues()
    documentForm.setDocumentValues(leave, customFields)
    await documentForm.create(documents)
  }

  const createLeave = async () => {
    const payload = {
      ...form.data(),
      hours_amount_in_cents: form.hoursAmount(),
    }

    const result = await asyncAction(() => leaves.create(payload), {
      successFlash: false,
    })

    if (isSuccessful(result)) {
      const { value } = result

      const leave = leaves.find({
        id: value.id,
      })

      if (!leave) {
        throw Error(`Error finding leaves with id = ${value.id}`)
      }

      // Medical leaves manage documents by them self
      if (onLeaveSuccess) {
        await onLeaveSuccess(leave)
      } else {
        await saveDocument(leave)
      }

      dispatch({
        type: EventTypes.LEAVE_CREATED,
        data: { leave: leave },
      })
      fetchStats(leave)
      fetchData(employee)
      onCloseYear()
    } else {
      onLeaveError()
    }
  }

  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(
    async () => {
      if (bulkAdd) {
        const payload = {
          ...form.data(),
          hours_amount_in_cents: form.hoursAmount(),
        }

        const { success } = await leaves.rpcCreateMultipleLeaves(payload, selectedEmployeeIds)

        if (success) onCloseYear()
      } else {
        await createLeave()
      }
    },
    {
      successFlash: false,
    }
  )

  return {
    onSubmit,
    isProcessing,
  }
}

export default useCreateLeave
